import React from 'react';
import PropTypes from 'prop-types';
import Icon from '../../Common/Icon';

const PRODUCT_SIGNALS = [
  { label: 'Energy', icon: 'zap' },
  { label: 'Routines', icon: 'repeat' },
  { label: 'Relationships', icon: 'heart' },
  { label: 'Creativity', icon: 'palette' },
  { label: 'Work', icon: 'briefcase' }
];

const TRUST_POINTS = [
  { icon: 'lock', text: 'Private by design' },
  { icon: 'bot', text: 'AI Coach guidance' },
  { icon: 'leaf', text: 'Real life results' }
];

const TRUST_STRIP = [
  { icon: 'lock', text: 'Your data stays private' },
  { icon: 'user', text: 'You own your insights' },
  { icon: 'leaf', text: 'Built for real life' },
  { icon: 'flask-conical', text: 'Science aligned' }
];

const LIFE_EXPERIMENTS = [
  { domain: 'Work', test: 'Phone-free first hour', signal: 'Energy before lunch', icon: 'briefcase' },
  { domain: 'Health', test: 'Screen-free wind-down', signal: 'Morning energy', icon: 'heart' },
  { domain: 'Relationships', test: 'Ask before reacting', signal: 'Repair time', icon: 'messages-square' },
  { domain: 'Creativity', test: '10-minute ugly draft', signal: 'Drafts started', icon: 'palette' },
  { domain: 'Learning', test: '15-minute skill sprint', signal: 'Practice kept', icon: 'graduation-cap' }
];

const LandingHero = React.memo(({ onSignup, onDesktop }) => (
  <section id="top" className="landing-hero">
    <div className="landing-template-frame landing-hero-frame">
      <div className="landing-hero-copy">
        <h1 className="landing-hero-title" aria-label="Run tiny experiments that show what works in your real life.">
          Run tiny experiments that show what works in your <span>real life</span>
        </h1>
        <p className="landing-hero-subtitle landing-sr-only">
          ThryveLoop helps you observe what is happening, design one small test, try it in daily life, and review the evidence across work, health, relationships, creativity, and learning.
        </p>

        <div className="landing-hero-rule" aria-hidden="true">
          <Icon name="sprout" size={24} />
          <span />
        </div>

        <div className="landing-hero-actions">
          <button type="button" className="landing-btn landing-btn--primary landing-hero-cta" onClick={onSignup}>
            Start free on Web
            <Icon name="arrow-right" size={15} />
          </button>
          <button type="button" className="landing-btn landing-btn--outline landing-hero-secondary" onClick={onDesktop}>
            Explore Desktop Lifetime
          </button>
        </div>

        <div className="landing-hero-trust" aria-label="ThryveLoop trust highlights">
          {TRUST_POINTS.map((point) => (
            <div key={point.text} className="landing-hero-trust-item">
              <span>
                <Icon name={point.icon} size={19} />
              </span>
              <p>{point.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="landing-hero-media">
        <img
          src="/landing/editorial-hero.jpg"
          alt="Woman reflecting by a bright window with a notebook and warm drink"
        />

        <article className="landing-preview-card landing-preview-card--loops" aria-label="My loops preview">
          <p className="landing-preview-title">My loops</p>
          <div className="landing-loop-orbit">
            <span className="landing-loop-center">
              <Icon name="refresh-cw" size={24} />
            </span>
            {PRODUCT_SIGNALS.map((signal, index) => (
              <span key={signal.label} className={`landing-loop-node landing-loop-node--${index + 1}`}>
                <Icon name={signal.icon} size={13} />
                <small>{signal.label}</small>
              </span>
            ))}
          </div>
        </article>

        <article className="landing-preview-card landing-preview-card--chart" aria-label="Current experiment preview">
          <div className="landing-preview-row">
            <p className="landing-preview-title">Current experiment</p>
            <span>Next</span>
          </div>
          <svg className="landing-chart" viewBox="0 0 320 94" aria-hidden="true">
            <path className="landing-chart-band" d="M128 16h30v64h-30z" />
            <path className="landing-chart-grid" d="M0 20H320M0 44H320M0 68H320" />
            <path className="landing-chart-line" d="M12 62L42 58L72 64L103 52L134 42L166 55L197 31L228 44L259 29L308 35" />
            <g className="landing-chart-points">
              <circle cx="12" cy="62" r="4" />
              <circle cx="42" cy="58" r="4" />
              <circle cx="72" cy="64" r="4" />
              <circle cx="103" cy="52" r="4" />
              <circle cx="134" cy="42" r="4" />
              <circle cx="166" cy="55" r="4" />
              <circle cx="197" cy="31" r="4" />
              <circle cx="228" cy="44" r="4" />
              <circle cx="259" cy="29" r="4" />
              <circle cx="308" cy="35" r="4" />
            </g>
          </svg>
        </article>

        <article className="landing-preview-card landing-preview-card--insight" aria-label="What I am learning preview">
          <p className="landing-preview-title">What I&apos;m learning</p>
          <div className="landing-insight-line">
            <span><Icon name="leaf" size={14} /></span>
            <i />
          </div>
          <div className="landing-insight-line">
            <span><Icon name="heart" size={14} /></span>
            <i />
          </div>
        </article>
      </div>
    </div>

    <div className="landing-trust-strip" aria-label="Platform promises">
      {TRUST_STRIP.map((point) => (
        <div key={point.text} className="landing-trust-strip-item">
          <Icon name={point.icon} size={18} />
          <span>{point.text}</span>
        </div>
      ))}
    </div>
  </section>
));

LandingHero.propTypes = {
  onSignup: PropTypes.func.isRequired,
  onDesktop: PropTypes.func.isRequired,
};

LandingHero.displayName = 'LandingHero';

export default LandingHero;
